import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { useAccountFeatureEntitlements } from '@dbt-labs/app-nav';
import { Link, NotificationBanner } from '@dbt-labs/sourdough';

import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlag } from '@/context/FeatureFlagProvider';
import { useAccountConnections } from '@/hooks/admin/connections';
import { useAdminDevStagingProdEnvs } from '@/hooks/admin/environments';
import { useHasNoMetadata } from '@/hooks/discovery/resourceCounts';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useUrlProjectParams, useValidatedParams } from '@/hooks/params';
import { useResolveProjectAndEnvironmentId } from '@/hooks/resolveProjectAndEnvironmentId';
import { DateTimeDismissableNotificationBanner } from './DateTimeDismissableNotificationBanner';
import { DismissableNotificationBanner } from './DismissableNotificationBanner';

interface MissingDataHeaderProps {
  missingSourceFreshness?: boolean;
  missingTestResults?: boolean;
  missingCatalogInfo?: boolean;
  className?: string;
}

export const MissingDataHeader: FC<MissingDataHeaderProps> = ({
  missingSourceFreshness,
  missingTestResults,
  missingCatalogInfo,
  className: _className,
}) => {
  const { defaultEnvironmentId, accountId, hasAccountFeature } = useDbtCloudAccount();
  const { projectId, environmentType } = useValidatedParams();

  const EXTERNAL_METADATA_BANNER_LOCAL_STORAGE_KEY = `dbt-cloud.explorer.external-metadata-banner-${projectId}`;

  const currentEnvironmentQuery = useResolveProjectAndEnvironmentId({
    accountId,
    projectId,
    environmentType,
  });
  const environmentId = currentEnvironmentQuery?.data?.environmentId;

  const adminEnvironments = useAdminDevStagingProdEnvs({
    accountId,
    projectId,
  }).data;
  const allowDevEnvToggle = useFeatureFlag(
    'meta3092ExplorerEnableDevEnvironment',
    false,
  );

  const hasCatalogIngestionEntitlement =
    !!useAccountFeatureEntitlements(accountId)?.data?.catalogIngestion;

  const hasNoMetadata = useHasNoMetadata({
    environmentId,
  });
  const projectScope = useUrlProjectParams();
  const getEnvironmentSettingsLink = useLinkToDbtCloud('environmentSettings');
  const settingsPageLink = useLinkToDbtCloud('accountSettings');
  const hasCatalogPreviewPermission = hasAccountFeature('explorerAccountUI');
  const hasCatalogIngestionPermission = hasAccountFeature('catalogIngestion');

  const shouldShowEnableNavigationBanner = !hasCatalogPreviewPermission;

  const shouldShowEnableCatalogIngestionBanner =
    hasCatalogPreviewPermission &&
    hasCatalogIngestionEntitlement &&
    !hasCatalogIngestionPermission;

  const snowflakeConnectionsQuery = useAccountConnections(
    accountId ?? 0,
    shouldShowEnableCatalogIngestionBanner,
    {
      adapterVersion: 'snowflake_v0',
    },
  );
  const hasSnowflakeConnections = !snowflakeConnectionsQuery.isFetching
    ? (snowflakeConnectionsQuery?.data?.length ?? 0) > 0
    : false;

  /**
   * Rather than using useDbtCloud().currentProjectId, we only
   * want to use the passed in defaultEnvironmentId if an
   * environment was not provided as a part of projectScope.
   */
  const projectIdFromUrl = projectScope.projectId;
  const missingDefaultEnvironment = Boolean(
    defaultEnvironmentId === 'NO_DEFAULT_ENVIRONMENT' && projectIdFromUrl,
  );

  const className = twMerge('m-2', _className);

  // to wait until we get all information we need before rendering
  if (!environmentId) {
    return null;
  }

  if (
    !allowDevEnvToggle &&
    environmentId &&
    environmentId === adminEnvironments?.development?.id
  ) {
    return (
      <NotificationBanner
        isGlobal={false}
        notification={{
          id: 'unsupportedEnvironment',
          type: 'warning',
          header: 'Unsupported Dev Environment View',
          helperText: (
            <p>
              The dev environment view is currently unsupported and will not have full
              functionality.
            </p>
          ),
        }}
      />
    );
  }

  const productName = 'Catalog';
  if (hasNoMetadata && environmentType !== 'development') {
    return (
      <div className={className}>
        <NotificationBanner
          isGlobal={false}
          notification={{
            id: 'noMetadataFound',
            type: 'warning',
            header: 'No Metadata Found',
            helperText: (
              <p>
                Please run a{' '}
                <Link
                  isInternal={false}
                  shouldOpenNewTab
                  to="https://docs.getdbt.com/docs/deploy/dbt-cloud-job"
                  colorVariant="warning"
                >
                  job
                </Link>{' '}
                in your {environmentType} environment to use dbt {productName}. dbt{' '}
                {productName} is powered by the latest artifacts from your job runs.
              </p>
            ),
          }}
        />
      </div>
    );
  }
  if (
    !missingDefaultEnvironment &&
    !missingCatalogInfo &&
    !missingSourceFreshness &&
    !missingTestResults &&
    !shouldShowEnableNavigationBanner &&
    !shouldShowEnableCatalogIngestionBanner
  ) {
    return null;
  }
  return (
    <div className={twMerge(className, 'mb-4 space-y-2')}>
      {missingDefaultEnvironment && (
        <NotificationBanner
          isGlobal={false}
          notification={{
            id: 'noDefaultEnvironment',
            type: 'warning',
            header: 'Project Has No Default Environment',
            helperText: (
              <p>
                Please set a production or staging environment in{' '}
                {getEnvironmentSettingsLink ? (
                  <Link
                    isInternal={false}
                    shouldOpenNewTab
                    to={getEnvironmentSettingsLink({
                      ...projectScope,
                      projectId: projectIdFromUrl,
                    })}
                  >
                    Environment Settings
                  </Link>
                ) : (
                  <>dbt Cloud Environment Settings</>
                )}{' '}
                in order to use {productName}. See{' '}
                <Link
                  isInternal={false}
                  shouldOpenNewTab
                  to={
                    'https://docs.getdbt.com/docs/deploy/deploy-environments#set-as-production-environment-beta'
                  }
                >
                  the docs
                </Link>{' '}
                for further instructions.
              </p>
            ),
          }}
        />
      )}
      {missingSourceFreshness && (
        <NotificationBanner
          isGlobal={false}
          notification={{
            id: 'missingSourceFreshness',
            type: 'warning',
            header: 'Missing Source Freshness',
            helperText: (
              <p>
                To view source freshness check results, define sources and run{' '}
                <span className="font-mono text-xs">dbt source freshness</span> within a
                job in the production or staging environment.
              </p>
            ),
          }}
        />
      )}
      {missingTestResults && (
        <NotificationBanner
          isGlobal={false}
          notification={{
            id: 'missingTestResults',
            type: 'warning',
            header: 'Missing Test Results',
            helperText: (
              <p>
                To view test results, define tests and run{' '}
                <span className="font-mono text-xs">dbt test</span> or{' '}
                <span className="font-mono text-xs">dbt build</span> within a job in the
                production or staging environment.
              </p>
            ),
          }}
        />
      )}
      {shouldShowEnableNavigationBanner && projectId && (
        <NavigationMigrationBanner
          projectId={projectId}
          accountId={accountId!}
          settingsPageLink={settingsPageLink}
        />
      )}
      {missingCatalogInfo && (
        <NotificationBanner
          isGlobal={false}
          notification={{
            id: 'missingCatalogInfo',
            type: 'warning',
            header: 'Missing Table Metadata',
            helperText: (
              <p>
                Please ensure that this resource and{' '}
                <span className="font-mono text-xs">docs generate</span> are run within
                job(s) in your{' '}
                <Link
                  isInternal={false}
                  shouldOpenNewTab
                  to="https://docs.getdbt.com/docs/deploy/deploy-environments#set-as-production-environment"
                >
                  production
                </Link>{' '}
                or staging environment. See{' '}
                <Link
                  isInternal={false}
                  shouldOpenNewTab
                  to="https://docs.getdbt.com/docs/collaborate/explore-projects#generate-metadata"
                >
                  the docs
                </Link>{' '}
                for more information.
              </p>
            ),
          }}
        />
      )}
      {shouldShowEnableCatalogIngestionBanner && hasSnowflakeConnections && (
        <DismissableNotificationBanner
          isGlobal={false}
          localStorageKey={EXTERNAL_METADATA_BANNER_LOCAL_STORAGE_KEY}
          notification={{
            id: 'enableCatalogNavigation',
            type: 'info',
            header: 'Ingest External Metadata into Catalog',
            helperText: (
              <p>
                Ingest metadata for non-dbt assets to power a unified end-to-end view of
                your data ecosystem. Your admin can enable the feature{' '}
                {settingsPageLink && (
                  <Link
                    isInternal={true}
                    shouldOpenNewTab
                    to={settingsPageLink({
                      accountId: accountId as number,
                    })}
                  >
                    here
                  </Link>
                )}
              </p>
            ),
          }}
        />
      )}
    </div>
  );
};

const NAVIGATION_MIGRATION_DEADLINE = new Date('2025-12-01T00:00:00Z');
const URGENT_THRESHOLD_DAYS = 7; // show persistently 7 days before deadline; cannot be dismissed

const WARNING_THRESHOLD_DAYS = 30; // add "(Required Dec 1)" tag 30 days before deadline
const TEMPORARY_DISMISSAL_HOURS = 24; // hide for 24 hours when dismissed (during non-urgent periods)

interface NavigationMigrationBannerProps {
  projectId: string | number;
  accountId: number;
  settingsPageLink?: ((params: { accountId: number }) => string) | null;
}

const NavigationMigrationBanner: FC<NavigationMigrationBannerProps> = ({
  projectId,
  accountId,
  settingsPageLink,
}) => {
  const GLOBAL_NAVIGATION_MIGRATION_BANNER_LOCAL_STORAGE_KEY = `dbt-cloud.explorer.global-navigation-migration-banner-${projectId}`;
  return (
    <DateTimeDismissableNotificationBanner
      isGlobal={false}
      localStorageKey={GLOBAL_NAVIGATION_MIGRATION_BANNER_LOCAL_STORAGE_KEY}
      deadlineDate={NAVIGATION_MIGRATION_DEADLINE}
      urgentThresholdDays={URGENT_THRESHOLD_DAYS}
      warningIndicatorText="(Required Dec 1)"
      warningThresholdDays={WARNING_THRESHOLD_DAYS}
      temporaryDismissalHours={TEMPORARY_DISMISSAL_HOURS}
      notification={{
        id: 'catalogNavigationMigration',
        type: 'info',
        header: 'Coming soon: A new navigation experience for dbt Catalog',
        helperText: (
          <p>
            On <strong>December 1st</strong>, all dbt platform accounts will see an
            updated Catalog navigation experience designed to simplify discovery and
            search across projects.
            <br />
            {settingsPageLink && (
              <Link
                isInternal={true}
                shouldOpenNewTab
                to={settingsPageLink({
                  accountId: accountId,
                })}
              >
                Try it now
              </Link>
            )}{' '}
            (admin required).{' '}
            <Link
              isInternal={false}
              shouldOpenNewTab
              to="https://docs.getdbt.com/docs/explore/global-navigation"
            >
              Learn more.
            </Link>
          </p>
        ),
      }}
    />
  );
};
